import { useEffect, useMemo, useState } from 'react'
import { api, fmt } from '../lib/api.js'
import { Chip, Email, Empty, ErrorNote, ModelPicker } from '../components/ui.jsx'
import ContactDialog from '../components/ContactDialog.jsx'

// Every contact the research collected, plus anything added by hand.
// A contact carried over from the Apollo export remembers how the last approach
// ended. Someone already told us no, so the list has to say so before anyone
// writes to them again.
const RULED_OUT = /pass|not a fit|unqualified/i
function priorContact(c) {
  if (!c.disposition) return null
  const out = RULED_OUT.test(c.disposition)
  return { out, label: c.disposition.replace(/^Pass[,;]?\s*/i, 'Passed ').trim(), at: c.outreach_at }
}

export function Contacts({ accounts, onOpen, onChanged }) {
  const [q, setQ] = useState('')
  const [hideOut, setHideOut] = useState(false)
  const [editing, setEditing] = useState(null)
  const ruledOut = useMemo(
    () => accounts.flatMap((a) => a.contacts || []).filter((c) => RULED_OUT.test(c.disposition || '')).length,
    [accounts],
  )
  const rows = useMemo(() => {
    const all = accounts.flatMap((a) =>
      (a.contacts || []).map((c) => ({ ...c, account: a.name, slug: a.slug, named: a.named })),
    )
    const needle = q.trim().toLowerCase()
    const kept = hideOut ? all.filter((c) => !RULED_OUT.test(c.disposition || '')) : all
    if (!needle) return kept
    return kept.filter(
      (c) =>
        (c.name || '').toLowerCase().includes(needle) ||
        (c.title || '').toLowerCase().includes(needle) ||
        c.account.toLowerCase().includes(needle),
    )
  }, [accounts, q, hideOut])

  return (
    <>
      <div className="topbar">
        <h1>Contacts</h1>
        <span className="topbar-note">{rows.length} shown</span>
        <div className="topbar-right">
          {ruledOut ? (
            <label className="row" style={{ gap: 5, fontSize: 'var(--fs-pill)', color: 'var(--muted)', cursor: 'pointer' }}>
              <input type="checkbox" checked={hideOut} onChange={(e) => setHideOut(e.target.checked)} />
              Hide {ruledOut} ruled out
            </label>
          ) : null}
          <input className="input" style={{ width: 240 }} placeholder="Search name, title or company" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>
      <div className="body" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  {/* Names are short; the address is the thing you came here to
                      read, so it gets the room. */}
                  <th style={{ width: 160 }}>Name</th>
                  <th style={{ width: 230 }}>Title</th>
                  <th style={{ width: 170 }}>Account</th>
                  <th>Email</th>
                  <th style={{ width: 92 }}>Phone</th>
                  <th style={{ width: 156 }}>Prior contact</th>
                  <th className="num" style={{ width: 54 }}>Named</th>
                  <th style={{ width: 70 }}>LinkedIn</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c, i) => (
                  <tr key={`${c.slug}-${c.name}-${i}`} onClick={() => setEditing(c)} title="Edit this contact">
                    <td style={{ fontWeight: 500, whiteSpace: 'normal' }}>
                      {c.name}
                      {c.origin === 'added' ? (
                        <span style={{ marginLeft: 6 }}>
                          <Chip kind="named">ADDED</Chip>
                        </span>
                      ) : null}
                      {c.is_placeholder ? (
                        <span style={{ marginLeft: 6 }}>
                          <Chip kind="absent">ROLE ABSENT</Chip>
                        </span>
                      ) : null}
                    </td>
                    <td style={{ color: 'var(--muted)', whiteSpace: 'normal' }}>{c.title}</td>
                    <td>
                      <button
                        className="btn link"
                        onClick={(ev) => {
                          ev.stopPropagation()
                          onOpen(c.slug)
                        }}
                        title="Open the account"
                      >
                        {c.account}
                      </button>
                    </td>
                    <td className="mono"><Email contact={c} /></td>
                    <td className="mono" style={{ color: c.phone ? 'var(--ink)' : 'var(--tertiary)' }}>{c.phone || '—'}</td>
                    <td>
                      {(() => {
                        const p = priorContact(c)
                        if (!p) return <span style={{ color: 'var(--tertiary)' }}>—</span>
                        return (
                          <span title={`${p.label}${p.at ? ` on ${p.at}` : ''}`}>
                            <Chip kind={p.out ? 'stale' : 'replied'}>{p.out ? 'RULED OUT' : 'APPROACHED'}</Chip>
                            {p.at ? <span className="mono" style={{ marginLeft: 5, fontSize: '10px', color: 'var(--tertiary)' }}>{p.at.slice(0, 7)}</span> : null}
                          </span>
                        )
                      })()}
                    </td>
                    <td className="num">{c.named}</td>
                    <td>
                      {c.linkedin ? (
                        <a href={c.linkedin} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>profile</a>
                      ) : (
                        <span style={{ color: 'var(--tertiary)' }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="row" style={{ padding: '8px 12px', borderTop: '0.5px solid var(--line)' }}>
            <span className="card-sub">Click a row to edit. Click the company to open the account. A dotted address is inferred from the company pattern and unconfirmed.</span>
          </div>
        </div>
      </div>

      {editing ? (
        <ContactDialog
          slug={editing.slug}
          accountName={editing.account}
          contact={editing}
          onClose={() => setEditing(null)}
          onSaved={onChanged}
        />
      ) : null}
    </>
  )
}

// overlay, so the bundle itself stays read-only.
export function Appendix({ onImported, models, model, onModel }) {
  const [rows, setRows] = useState(null)
  const [queue, setQueue] = useState({})
  const [q, setQ] = useState('')
  const [picked, setPicked] = useState(() => new Set())
  const [brief, setBrief] = useState(null)
  const [importText, setImportText] = useState('')
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const [adding, setAdding] = useState(null)
  const [estimate, setEstimate] = useState(null)
  const [compare, setCompare] = useState(null)

  const load = () => {
    api.appendix().then(setRows).catch(() => setRows([]))
    api.researchLog().then((d) => setQueue(d.queue || {})).catch(() => {})
  }
  useEffect(() => {
    load()
  }, [])

  const shown = useMemo(() => {
    if (!rows) return []
    const pending = rows.filter((r) => r.status === 'Not yet researched')
    const needle = q.trim().toLowerCase()
    if (!needle) return pending
    return pending.filter(
      (r) => (r.company || '').toLowerCase().includes(needle) || (r.subcategory || '').toLowerCase().includes(needle),
    )
  }, [rows, q])

  useEffect(() => {
    if (!picked.size) {
      setEstimate(null)
      return
    }
    api.costEstimate({ kind: 'dossier', count: picked.size, model }).then(setEstimate).catch(() => setEstimate(null))
    api.costCompare({ kind: 'dossier', count: picked.size }).then(setCompare).catch(() => setCompare(null))
  }, [picked, model])

  const saveCompany = async () => {
    await api.addCompany(adding)
    setAdding(null)
    load()
  }

  const toggle = (company) =>
    setPicked((s) => {
      const next = new Set(s)
      next.has(company) ? next.delete(company) : next.add(company)
      return next
    })

  const queueResearch = async () => {
    setBusy(true)
    setError(null)
    try {
      const out = await api.queueResearch([...picked], model)
      setBrief(out)
      setPicked(new Set())
      load()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  const importResearch = async () => {
    setBusy(true)
    setError(null)
    try {
      const trimmed = importText.trim()
      const body = trimmed.startsWith('{') || trimmed.startsWith('[') ? { records: JSON.parse(trimmed) } : { path: trimmed }
      const out = await api.importResearch(body)
      setImportText('')
      load()
      onImported?.()
      setBrief({ imported: out.imported, slugs: out.slugs })
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="topbar">
        <h1>Not researched</h1>
        <span className="topbar-note">
          {shown.length} on the master list{picked.size ? ` · ${picked.size} selected` : ''}
        </span>
        <div className="topbar-right">
          <input className="input" style={{ width: 220 }} placeholder="Search company or segment" value={q} onChange={(e) => setQ(e.target.value)} />
          <button className="btn" onClick={() => setAdding({ company: '' })}>+ Add company</button>
          <button className="btn primary" disabled={!picked.size || busy} onClick={queueResearch}>
            Research {picked.size || ''}
            {estimate && picked.size ? <span className="mono" style={{ marginLeft: 5 }}>{estimate.usd_display}</span> : null}
          </button>
        </div>
      </div>

      <div className="body" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <ErrorNote error={error} />

        {brief ? (
          <div className="notice" style={{ marginBottom: 12, display: 'block' }}>
            {brief.brief_path ? (
              <>
                <div className="notice-title">Brief written for {brief.queued} companies</div>
                <div className="notice-body" style={{ marginTop: 3 }}>
                  Hand this file to a research agent, then paste the returned JSON below to bring them into Accounts.
                </div>
                <div className="mono" style={{ fontSize: 'var(--fs-badge)', marginTop: 6, color: 'var(--ai-ink)' }}>{brief.brief_path}</div>
              </>
            ) : (
              <div className="notice-title">Imported {brief.imported} companies. They are in Accounts now.</div>
            )}
            <button className="btn" style={{ marginTop: 8 }} onClick={() => setBrief(null)}>Dismiss</button>
          </div>
        ) : null}

        <div className="notice" style={{ marginBottom: 12, display: 'block' }}>
          <div className="notice-title">These have never been researched</div>
          <div className="notice-body" style={{ marginTop: 3 }}>
            No focus areas, no contacts, no evidence. Figures here come from the master spreadsheet, which has known
            errors, so they are shown but not trusted.
          </div>
          <div className="notice-body" style={{ marginTop: 6 }}>
            <b>How research works here.</b> Tick companies and press Research: the app writes a brief from the SPEC
            prompt naming exactly those companies. You hand that file to a research agent. It returns one JSON record
            per company, in the same shape as the existing dossiers.{' '}
            <b>Import completed research</b> is where that JSON comes back in. Paste it, or give the path to the file,
            and those companies join the Accounts list with today as their researched date. Nothing is written into the
            research bundle.
          </div>
        </div>

        {adding ? (
          <div className="card" style={{ marginBottom: 12 }}>
            <div className="card-header">
              <span className="card-title">Add a company to the list</span>
              <span className="card-sub">for names the master list never had</span>
            </div>
            <div className="card-body stack" style={{ gap: 7 }}>
              {[['company', 'Company name'], ['subcategory', 'Segment, e.g. Distribution'], ['country', 'Country'], ['ownership', 'Ownership']].map(
                ([key, label]) => (
                  <input
                    key={key}
                    className="input"
                    placeholder={label}
                    value={adding[key] || ''}
                    onChange={(e) => setAdding({ ...adding, [key]: e.target.value })}
                  />
                ),
              )}
              <div className="row">
                <button className="btn primary" onClick={saveCompany} disabled={!(adding.company || '').trim()}>Add to list</button>
                <button className="btn" onClick={() => setAdding(null)}>Cancel</button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 30 }}></th>
                  <th>Company</th>
                  <th>Segment</th>
                  <th>Country</th>
                  <th className="num">Sheet revenue $m</th>
                  <th>Ownership</th>
                  <th>Research</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r, i) => {
                  const queued = queue[r.company]
                  return (
                    <tr key={`${r.company}-${i}`} onClick={() => toggle(r.company)} className={picked.has(r.company) ? 'on' : ''}>
                      <td>
                        <input type="checkbox" readOnly checked={picked.has(r.company)} style={{ accentColor: 'var(--accent)' }} />
                      </td>
                      <td style={{ fontWeight: 500 }}>{r.company}</td>
                      <td style={{ color: 'var(--muted)' }}>{r.subcategory || r.category}</td>
                      <td style={{ color: 'var(--muted)' }}>{r.country || '—'}</td>
                      <td className="num" style={{ color: 'var(--tertiary)' }}>{r.sheet_rev_m ?? '—'}</td>
                      <td>{r.ownership || '—'}</td>
                      <td>
                        {queued ? <Chip kind="awaiting">QUEUED {fmt.date(queued.requested_at)}</Chip> : <span className="chip absent">—</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div style={{ padding: '10px 12px', borderTop: '0.5px solid var(--line)', flexShrink: 0 }}>
            {estimate ? (
              <div style={{ marginBottom: 10 }}>
                <div className="card-sub" style={{ marginBottom: 6 }}>
                  {estimate.count} selected · estimated {estimate.usd_display} on {estimate.model_label}, about $
                  {estimate.per_unit_usd.toFixed(2)} per company. {estimate.caveat}
                </div>
                <ModelPicker value={model} options={models} onChange={onModel} compare={compare} count={picked.size} />
              </div>
            ) : null}
            <div className="lbl" style={{ marginBottom: 6 }}>Import completed research</div>
            <div className="row" style={{ alignItems: 'flex-start' }}>
              <textarea
                className="input"
                style={{ minHeight: 40 }}
                placeholder="Paste the returned JSON records, or the path to a batch file"
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
              />
              <button className="btn primary" onClick={importResearch} disabled={!importText.trim() || busy}>
                Import
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

// Append-only record of what was researched, when, and what changed. This is
// also the spine a future re-scan diff would hang off.
export function ResearchLog() {
  const [data, setData] = useState(null)
  const [busy, setBusy] = useState(false)

  const load = () => api.researchLog().then(setData).catch(() => setData({ log: [], queue: {}, schedule: null }))
  useEffect(() => {
    load()
  }, [])

  const setCadence = async (cadence) => {
    setBusy(true)
    await api.setSchedule({ cadence })
    await load()
    setBusy(false)
  }

  if (!data) return <div className="body"><Empty>Loading…</Empty></div>

  const queued = Object.entries(data.queue || {})

  return (
    <>
      <div className="topbar">
        <h1>Research log</h1>
        <span className="topbar-note">{data.log.length} entries</span>
      </div>
      <div className="body split">
        <div className="col">
          <div className="card">
            <div className="card-header">
              <span className="card-title">What has been run</span>
              <span className="card-sub">newest first</span>
            </div>
            <div className="card-body">
              {data.log.length === 0 ? (
                <Empty>Nothing logged yet. The original research was done before this log existed.</Empty>
              ) : (
                data.log.map((row) => (
                  <div key={row.id} className="row" style={{ alignItems: 'baseline', padding: '6px 0', borderBottom: '0.5px solid var(--line-soft)' }}>
                    <span className="mono" style={{ fontSize: 'var(--fs-badge)', color: 'var(--muted)', width: 62 }}>
                      {fmt.date(row.at).toUpperCase()}
                    </span>
                    <Chip kind={row.kind === 'import' ? 'named' : row.kind === 'queued' ? 'awaiting' : 'plain'}>
                      {row.kind.toUpperCase()}
                    </Chip>
                    <span style={{ fontSize: 'var(--fs-pill)' }}>{row.note}</span>
                    {row.accounts?.length ? (
                      <span className="card-sub" style={{ marginLeft: 'auto' }}>{row.accounts.length} accounts</span>
                    ) : null}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="rail" style={{ padding: 14 }}>
          <div className="card">
            <div className="card-header"><span className="card-title">Top-up cadence</span></div>
            <div className="card-body">
              <div style={{ fontSize: 'var(--fs-pill)', color: 'var(--ink-soft)', marginBottom: 9 }}>
                How often the researched accounts get re-read. A run re-reads filings and calls, then stamps each
                account's last-updated date.
              </div>
              <div className="row" style={{ marginBottom: 9 }}>
                {['monthly', 'quarterly', 'biannual', 'annual'].map((c) => (
                  <button key={c} className={`btn ${data.schedule?.cadence === c ? 'on' : ''}`} disabled={busy} onClick={() => setCadence(c)}>
                    {c[0].toUpperCase() + c.slice(1)}
                  </button>
                ))}
              </div>
              {data.schedule ? (
                <div className="mono" style={{ fontSize: 'var(--fs-pill)', color: data.schedule.due_now ? 'var(--warn)' : 'var(--muted)' }}>
                  next due {fmt.date(data.schedule.next_due)}
                  {data.schedule.due_now ? ' — due now' : ''}
                </div>
              ) : (
                <div className="card-sub">No cadence set.</div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">Queued</span>
              <span className="card-sub">{queued.length}</span>
            </div>
            <div className="card-body">
              {queued.length === 0 ? (
                <Empty>Nothing queued.</Empty>
              ) : (
                queued.map(([company, meta]) => (
                  <div className="row" key={company} style={{ padding: '3px 0' }}>
                    <span style={{ fontSize: 'var(--fs-pill)' }}>{company}</span>
                    <span className="card-sub" style={{ marginLeft: 'auto' }}>{fmt.date(meta.requested_at)}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export function Replies({ onOpen }) {
  const [rows, setRows] = useState(null)
  useEffect(() => {
    api.replies().then(setRows).catch(() => setRows([]))
  }, [])

  return (
    <>
      <div className="topbar">
        <h1>Replies</h1>
        <span className="topbar-note">{rows?.length ?? 0} logged</span>
      </div>
      <div className="body">
        {!rows?.length ? (
          <Empty>Nothing logged yet. Replies are entered by hand from the campaign screen.</Empty>
        ) : (
          <div className="card">
            <div className="card-body stack">
              {rows.map((r) => (
                <div key={r.id} style={{ borderLeft: '2px solid var(--blue)', paddingLeft: 12 }}>
                  <div className="row" style={{ alignItems: 'baseline' }}>
                    <span style={{ fontWeight: 600 }}>{r.account_name}</span>
                    <span className="card-sub">{fmt.date(r.at)}</span>
                    <Chip kind={r.sentiment === 'positive' ? 'named' : r.sentiment === 'not_now' ? 'stale' : 'plain'}>
                      {r.sentiment.replace('_', ' ').toUpperCase()}
                    </Chip>
                    <button className="btn link" style={{ marginLeft: 'auto' }} onClick={() => onOpen(r.account_slug)}>Open account</button>
                  </div>
                  <div style={{ fontSize: 'var(--fs-pill)', color: 'var(--ink-soft)', marginTop: 4 }}>{r.note}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
